import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/atoms';
import WarningPopup from 'components/molecules/popup/warning-popup/WarningPopup';
import { Form } from 'components/organisms';
import { musicProductDigitalTabs } from 'components/organisms/forms/music-product-digital/MusicProductDigitalForm';
import { listQueryKeys } from 'constants/queryKey';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import middleware from 'middleware';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePostMusicProductDigital } from 'services/music-product-digital';
import { usePostMusicProductDigitalImage } from 'services/music-product-digital/image';
import { usePostMusicProductDigitalImageThumbnail } from 'services/music-product-digital/imageThumbnail';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { IMusicProductDigitalResponse } from 'services/music-product-digital/types/query';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowToast } from 'utils/service/errorHandler';

const CreateMusicProductDigital = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { musicProductDigitalForm, clearForms } = useStoreState();

  const createMusicProdutc = usePostMusicProductDigital();
  const addImage = usePostMusicProductDigitalImage();
  const addImageThumbnail = usePostMusicProductDigitalImageThumbnail();

  const queryClient = useQueryClient();

  const mutateOptions = useMutateOptions<IMusicProductDigitalForm>();

  const goToIndividualMusicProductDigital = async (data: IMusicProductDigitalResponse) => {
    clearForms();
    navigate(
      `/music-product-digital/${data.id}/${data.releaseStatus === STATUS.draft ? getLastPathname(pathname) : 'delivery'}`,
    );
    await queryClient.invalidateQueries({
      queryKey: [listQueryKeys.musicProductDigital],
    });
  };

  const onCreateMusicProductDigitalWithFile = async (
    data: IMusicProductDigitalResponse,
    file?: File,
    thumbnailFile?: File,
  ) => {
    file &&
      (await addImage.mutateAsync(
        { file, id: data.id, version: data.version },
        {
          onError: (error) => {
            onErrorShowToast(error, 'toast-message.add-album-cover');
          },
        },
      ));
    thumbnailFile &&
      (await addImageThumbnail.mutateAsync(
        { file: thumbnailFile, id: data.id, version: data.version },
        {
          onError: (error) => {
            onErrorShowToast(error, 'toast-message.add-album-cover');
          },
        },
      ));

    await goToIndividualMusicProductDigital(data);
  };

  const onCreateMusicProductDigital = async (
    values: IMusicProductDigitalForm,
    helpers: FormikHelpers<IMusicProductDigitalForm>,
  ) => {
    const musicProductDigitalMutation = {
      ...middleware.musicProductDigital.formToQuery(values),
      waitingForFile: !!values.file,
    };

    await createMusicProdutc.mutateAsync(musicProductDigitalMutation, {
      ...mutateOptions(helpers),
      onSuccess: async (data) =>
        values.file || values.thumbnailFile
          ? onCreateMusicProductDigitalWithFile(data, values.file, values.thumbnailFile)
          : await goToIndividualMusicProductDigital(data),
    });
  };

  return (
    <Form.MusicProductDigital
      i18nextPrefix="music-product-digital"
      onSubmit={onCreateMusicProductDigital}
      disableTabs={[
        musicProductDigitalTabs.delivery,
        musicProductDigitalTabs.statistics,
        musicProductDigitalTabs.contracts,
      ]}
      initialValues={
        musicProductDigitalForm
          ? { ...musicProductDigitalForm, releaseStatus: STATUS.draft }
          : undefined
      }
      tittle={t('music-product-digital.title.create')}
      customBackItem={
        <WarningPopup
          title={t('popup.form-cancelation-title')}
          message={t('popup.form-cancelation-description')}
          onConfirm={() => {
            navigate('/music-product-digital');
            clearForms();
          }}
          confirmButtonVariant={BUTTON_VARIANT.background}
          trigger={
            <Button onlyIcon isClear>
              <Button.Icon icon={<PiX />} />
            </Button>
          }
        />
      }
    />
  );
};

export default CreateMusicProductDigital;
